import { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import Select from 'react-select';
import { useCSVDownloader } from 'react-papaparse';
import { useTranslation } from 'react-i18next';

import { PageHeading } from '../components/common/PageHeading';
import { ConfirmDialog } from '../components/common/ConfirmDialog';
import { TrashIcon } from '../components/icons/ServiceIcons';
import { get, post } from '../lib/api';
import { toLocalDate } from '../lib/dates';
import { usePageTitle } from '../lib/usePageTitle';
import type { SelectOption } from '../components/form';

interface UserRow {
    email: string;
    phoneNumber: string;
    createdAt: number;
    emailConfirmed: string;
    name: string;
    lastLoginDate: number;
    customerName: string;
}

type Confirmed = 'all' | 'yes' | 'no';
type SortKey = 'name' | 'email' | 'phoneNumber' | 'lastLoginDate' | 'customerName' | 'createdAt' | 'emailConfirmed';

// CSV export is intentionally gated behind a deliberately self-deprecating code
// word: exporting the user list is discouraged, but occasionally needed under
// controlled circumstances.
const EXPORT_CODE = 'i am an idiot';

export function UsersPage() {
    const { t } = useTranslation();
    usePageTitle(t('users.title'));
    const { CSVDownloader, Type } = useCSVDownloader();

    const [allData, setAllData] = useState<UserRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [confirmed, setConfirmed] = useState<Confirmed>('all');
    const [search, setSearch] = useState('');
    const [customers, setCustomers] = useState<string[]>([]);
    const [createdAfter, setCreatedAfter] = useState('');
    const [loginAfter, setLoginAfter] = useState('');
    // Default to newest first — the most recently created users are usually what
    // an operator is looking for right after registering or importing.
    const [sortKey, setSortKey] = useState<SortKey>('createdAt');
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
    const [exportCode, setExportCode] = useState('');
    const [deleteTarget, setDeleteTarget] = useState<UserRow | null>(null);
    const [deleting, setDeleting] = useState(false);
    const [actionMsg, setActionMsg] = useState('');
    const [selectedEmails, setSelectedEmails] = useState<string[]>([]);
    const [showBulkDelete, setShowBulkDelete] = useState(false);
    const [bulkDeleting, setBulkDeleting] = useState(false);

    function loadUsers() {
        setLoading(true);
        get<UserRow[]>('home/getLocalUsers')
            .then((res) =>
                setAllData((res.data ?? []).map((d) => ({ ...d, emailConfirmed: String(d.emailConfirmed) }))),
            )
            .catch(() => setError(t('users.error')))
            .finally(() => setLoading(false));
    }

    useEffect(() => {
        loadUsers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function confirmDelete() {
        if (!deleteTarget) return;
        setDeleting(true);
        setError('');
        try {
            await post('activeDirectory/Delete', { Email: deleteTarget.email });
            setActionMsg(t('users.deleted'));
            setDeleteTarget(null);
            loadUsers();
        } catch {
            setError(t('users.deleteError'));
        } finally {
            setDeleting(false);
        }
    }

    async function confirmBulkDelete() {
        setBulkDeleting(true);
        setError('');
        try {
            const count = selectedEmails.length;
            await post('activeDirectory/DeleteUsers', { emails: selectedEmails });
            setShowBulkDelete(false);
            setSelectedEmails([]);
            setActionMsg(t('users.bulkDeleted', { count }));
            loadUsers();
        } catch {
            setShowBulkDelete(false);
            setError(t('users.bulkDeleteError'));
        } finally {
            setBulkDeleting(false);
        }
    }

    const customerOptions = useMemo<SelectOption[]>(() => {
        const seen = new Set<string>();
        const opts: SelectOption[] = [];
        for (const d of allData) {
            if (d.customerName && !seen.has(d.customerName)) {
                seen.add(d.customerName);
                opts.push({ label: d.customerName, value: d.customerName });
            }
        }
        return opts;
    }, [allData]);

    const confirmedOptions: SelectOption[] = [
        { label: t('users.confirmedAll'), value: 'all' },
        { label: t('users.confirmedYes'), value: 'yes' },
        { label: t('users.confirmedNo'), value: 'no' },
    ];

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        const createdTicks = createdAfter ? new Date(createdAfter).getTime() : new Date(2010, 0, 1).getTime();
        const loginTicks = loginAfter ? new Date(loginAfter).getTime() : 0;
        return allData.filter((d) => {
            if (confirmed === 'yes' && d.emailConfirmed !== 'true') return false;
            if (confirmed === 'no' && d.emailConfirmed !== 'false') return false;
            if (
                q &&
                ![d.name, d.email, d.phoneNumber, d.customerName].some((v) => (v ?? '').toLowerCase().includes(q))
            )
                return false;
            if (customers.length && !customers.includes(d.customerName)) return false;
            if (d.createdAt && d.createdAt <= createdTicks) return false;
            if (loginTicks) {
                if (!d.lastLoginDate || d.lastLoginDate <= loginTicks) return false;
            }
            return true;
        });
    }, [allData, confirmed, search, customers, createdAfter, loginAfter]);

    const sorted = useMemo(() => {
        const rows = [...filtered];
        const dir = sortDir === 'asc' ? 1 : -1;
        const numeric = sortKey === 'lastLoginDate' || sortKey === 'createdAt';
        rows.sort((a, b) => {
            if (numeric) return (((a[sortKey] as number) || 0) - ((b[sortKey] as number) || 0)) * dir;
            return (
                String(a[sortKey] ?? '').localeCompare(String(b[sortKey] ?? ''), 'nb', { sensitivity: 'base' }) *
                dir
            );
        });
        return rows;
    }, [filtered, sortKey, sortDir]);

    // Keep the selection scoped to currently-visible rows, so a bulk delete only
    // ever affects what the admin can see (filtering a row out deselects it).
    useEffect(() => {
        const visible = new Set(sorted.map((d) => d.email));
        setSelectedEmails((prev) => {
            const next = prev.filter((e) => visible.has(e));
            return next.length === prev.length ? prev : next;
        });
    }, [sorted]);

    const allVisibleSelected = sorted.length > 0 && sorted.every((d) => selectedEmails.includes(d.email));
    function toggleRow(email: string) {
        setSelectedEmails((prev) =>
            prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email],
        );
    }
    function toggleSelectAll() {
        setSelectedEmails(allVisibleSelected ? [] : sorted.map((d) => d.email));
    }

    function toggleSort(key: SortKey) {
        if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        else {
            setSortKey(key);
            setSortDir('asc');
        }
    }

    const columns: { key: SortKey; label: string }[] = [
        { key: 'name', label: t('users.colName') },
        { key: 'email', label: t('users.colEmail') },
        { key: 'phoneNumber', label: t('users.colPhone') },
        { key: 'lastLoginDate', label: t('users.colLastLogin') },
        { key: 'customerName', label: t('users.colCustomer') },
        { key: 'createdAt', label: t('users.colCreated') },
        { key: 'emailConfirmed', label: t('users.colConfirmed') },
    ];

    const exportUnlocked = exportCode.trim().toLowerCase() === EXPORT_CODE;
    const downloadData = () =>
        sorted.map((d) => ({
            Name: d.name,
            Email: d.email,
            Phone: d.phoneNumber,
            LastLoginDate: d.lastLoginDate ? toLocalDate(d.lastLoginDate) : '',
            CustomerName: d.customerName,
            CreatedAt: toLocalDate(d.createdAt),
            EmailConfirmed: d.emailConfirmed,
        }));

    return (
        <>
            <PageHeading title={t('users.title')} meta={t('users.count', { count: sorted.length })} />

            <Row className="g-3 mb-3">
                <Col xs={12} lg={4}>
                    <Form.Label htmlFor="f-search">{t('users.search')}</Form.Label>
                    <Form.Control
                        id="f-search"
                        type="search"
                        placeholder={t('users.searchPlaceholder')}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </Col>
                <Col xs={12} md={6} lg={4}>
                    <Form.Label htmlFor="f-confirmed">{t('users.filterConfirmed')}</Form.Label>
                    <Select
                        inputId="f-confirmed"
                        options={confirmedOptions}
                        classNamePrefix="dkn-rs"
                        noOptionsMessage={() => t('common.noOptions')}
                        value={confirmedOptions.find((o) => o.value === confirmed) ?? null}
                        onChange={(o) => setConfirmed(((o as SelectOption | null)?.value as Confirmed) ?? 'all')}
                    />
                </Col>
                <Col xs={12} md={6} lg={4}>
                    <Form.Label htmlFor="f-customers">{t('users.customers')}</Form.Label>
                    <Select
                        inputId="f-customers"
                        isMulti
                        options={customerOptions}
                        classNamePrefix="dkn-rs"
                        placeholder={t('users.confirmedAll')}
                        noOptionsMessage={() => t('common.noOptions')}
                        value={customerOptions.filter((o) => customers.includes(o.value))}
                        onChange={(vals) => setCustomers((vals as SelectOption[]).map((v) => v.value))}
                    />
                </Col>
                <Col xs={12} md={6} lg={4}>
                    <Form.Label htmlFor="f-created">{t('users.createdAfter')}</Form.Label>
                    <Form.Control
                        id="f-created"
                        type="date"
                        value={createdAfter}
                        onChange={(e) => setCreatedAfter(e.target.value)}
                    />
                </Col>
                <Col xs={12} md={6} lg={4}>
                    <Form.Label htmlFor="f-login">{t('users.loginAfter')}</Form.Label>
                    <Form.Control
                        id="f-login"
                        type="date"
                        value={loginAfter}
                        onChange={(e) => setLoginAfter(e.target.value)}
                    />
                </Col>
            </Row>

            {error && <Alert variant="danger">{error}</Alert>}
            {actionMsg && (
                <Alert variant="success" dismissible onClose={() => setActionMsg('')}>
                    {actionMsg}
                </Alert>
            )}

            {!loading && sorted.length > 0 && (
                <div className="mb-2">
                    <Button
                        variant="outline-danger"
                        size="sm"
                        disabled={selectedEmails.length === 0}
                        onClick={() => setShowBulkDelete(true)}
                    >
                        {t('users.deleteSelected', { count: selectedEmails.length })}
                    </Button>
                </div>
            )}

            {loading ? (
                <div className="text-body-secondary d-flex align-items-center gap-2">
                    <Spinner animation="border" size="sm" aria-hidden="true" /> {t('users.loading')}
                </div>
            ) : sorted.length > 0 ? (
                <div className="table-responsive users-table">
                    <Table striped bordered hover size="sm" className="align-middle mb-0">
                        <thead>
                            <tr>
                                <th className="users-table__num">
                                    <Form.Check
                                        type="checkbox"
                                        aria-label={t('users.selectAllUsers')}
                                        checked={allVisibleSelected}
                                        onChange={toggleSelectAll}
                                    />
                                </th>
                                <th className="users-table__num">#</th>
                                {columns.map((c) => (
                                    <th
                                        key={c.key}
                                        role="button"
                                        aria-sort={
                                            sortKey === c.key
                                                ? sortDir === 'asc'
                                                    ? 'ascending'
                                                    : 'descending'
                                                : 'none'
                                        }
                                        onClick={() => toggleSort(c.key)}
                                        className="users-table__sort"
                                    >
                                        {c.label}
                                        <span className="users-table__arrow" aria-hidden="true">
                                            {sortKey === c.key ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                                        </span>
                                    </th>
                                ))}
                                <th className="users-table__num">
                                    <span className="visually-hidden">{t('users.delete')}</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {sorted.map((d, index) => (
                                <tr key={d.email || index}>
                                    <td>
                                        <Form.Check
                                            type="checkbox"
                                            aria-label={t('users.selectUser')}
                                            checked={selectedEmails.includes(d.email)}
                                            onChange={() => toggleRow(d.email)}
                                        />
                                    </td>
                                    <td>{index + 1}</td>
                                    <td>{d.name}</td>
                                    <td>{d.email}</td>
                                    <td>{d.phoneNumber}</td>
                                    <td>{d.lastLoginDate ? toLocalDate(d.lastLoginDate) : '–'}</td>
                                    <td>{d.customerName}</td>
                                    <td>{toLocalDate(d.createdAt)}</td>
                                    <td>
                                        {d.emailConfirmed === 'true' ? (
                                            <span className="badge text-bg-success">✓</span>
                                        ) : (
                                            <span className="badge text-bg-secondary">–</span>
                                        )}
                                    </td>
                                    <td className="text-end">
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            className="py-0 px-1"
                                            aria-label={t('users.delete')}
                                            title={t('users.delete')}
                                            onClick={() => setDeleteTarget(d)}
                                        >
                                            <TrashIcon style={{ width: 16, height: 16, verticalAlign: '-3px' }} />
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            ) : (
                !error && <Alert variant="info">{t('users.noUsers')}</Alert>
            )}

            <div className="mt-4 pt-3 border-top" style={{ maxWidth: '22rem' }}>
                <Form.Label htmlFor="export-code" className="text-body-secondary small">
                    {t('users.exportCodeword')}
                </Form.Label>
                <div className="d-flex gap-2 align-items-center">
                    <Form.Control
                        id="export-code"
                        size="sm"
                        autoComplete="off"
                        value={exportCode}
                        onChange={(e) => setExportCode(e.target.value)}
                    />
                    {exportUnlocked && sorted.length > 0 && (
                        <CSVDownloader
                            type={Type.Button}
                            filename="dekon-sso-users"
                            bom
                            data={downloadData}
                            className="btn btn-outline-primary btn-sm text-nowrap"
                        >
                            {t('users.download')}
                        </CSVDownloader>
                    )}
                </div>
            </div>

            <ConfirmDialog
                show={!!deleteTarget}
                title={t('users.deleteTitle')}
                confirmLabel={t('users.delete')}
                onCancel={() => setDeleteTarget(null)}
                onConfirm={confirmDelete}
                confirmDisabled={deleting}
            >
                {t('users.confirmDelete', {
                    name: deleteTarget?.name?.trim() || deleteTarget?.email || '',
                })}
            </ConfirmDialog>

            <ConfirmDialog
                show={showBulkDelete}
                title={t('users.bulkDeleteTitle')}
                confirmLabel={t('users.delete')}
                onCancel={() => setShowBulkDelete(false)}
                onConfirm={confirmBulkDelete}
                confirmDisabled={bulkDeleting}
            >
                {t('users.confirmBulkDelete', { count: selectedEmails.length })}
            </ConfirmDialog>
        </>
    );
}
